import { AIOutcome, AIIntent } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { i18next } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { getAiOutcomeIcon, OUTCOME_MAPPING } from '../icons/utils';

export const AiOutcomeComponent = ({ outcome, info }: { outcome: AIOutcome; info?: string }) => {
  const config = AI_OUTCOME_CONFIG[outcome];

  if (!config) return null;

  return (
    <div style={{ textAlign: 'end', display: 'grid', gap: theme.spacing(0.5) }}>
      <OutcomeDisplay config={config} />
      {info && (
        <Truncated as={Text} size='small' color='subdued'>
          {info}
        </Truncated>
      )}
    </div>
  );
};

type OutcomeConfig = {
  outcome: (typeof OUTCOME_MAPPING)[keyof typeof OUTCOME_MAPPING];
  text: string;
  isBold?: boolean;
  secondaryText?: string;
};

const AI_OUTCOME_CONFIG: Record<string, OutcomeConfig> = {
  [AIOutcome.AI_OUTCOME_END_CALL]: {
    outcome: OUTCOME_MAPPING.SUCCESS,
    text: i18next.t('Successfully assisted the patient', { ns: 'calls' }),
  },
  [AIOutcome.AI_OUTCOME_FATAL]: {
    outcome: OUTCOME_MAPPING.FATAL,
    text: i18next.t('Error', { ns: 'calls' }),
    isBold: true,
  },
  [AIOutcome.AI_OUTCOME_ROUTE_MISSED]: {
    outcome: OUTCOME_MAPPING.ROUTE_MISSED,
    text: i18next.t('Needs attention: ', { ns: 'calls' }),
    secondaryText: i18next.t('Transfer missed', { ns: 'calls' }),
    isBold: true,
  },
  [AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED]: {
    outcome: OUTCOME_MAPPING.ROUTE_ANSWERED,
    text: i18next.t('Call transferred: ', { ns: 'calls' }),
    secondaryText: i18next.t('Office assisted', { ns: 'calls' }),
    isBold: true,
  },
  [AIOutcome.AI_OUTCOME_TEXT_HANDOFF]: {
    outcome: OUTCOME_MAPPING.TEXT_HANDOFF,
    text: i18next.t('Call transferred: ', { ns: 'calls' }),
    secondaryText: i18next.t('Continued over text', { ns: 'calls' }),
    isBold: true,
  },
  [AIOutcome.AI_OUTCOME_INCOMPLETE]: {
    outcome: OUTCOME_MAPPING.INCOMPLETE,
    text: i18next.t('Incomplete: ', { ns: 'calls' }),
    secondaryText: i18next.t('Caller hung up', { ns: 'calls' }),
    isBold: true,
  },
};

const OutcomeDisplay = ({ config }: { config: OutcomeConfig }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    }}
  >
    <div style={{ justifySelf: 'end', display: 'grid' }}>{getAiOutcomeIcon({ outcome: config.outcome })}</div>
    <div>
      <Text as='span' size='medium' weight={config.isBold ? 'bold' : undefined}>
        {config.text}
      </Text>
      {config.secondaryText && (
        <Text as='span' size='medium'>
          {config.secondaryText}
        </Text>
      )}
    </div>
  </div>
);

export const getAIIntentLabel = (intent: AIIntent): Record<string, string> => {
  const intentMap: Partial<Record<AIIntent, { name: string; indicatorColor: string }>> = {
    [AIIntent.AI_INTENT_REPRESENTATIVE]: {
      name: i18next.t('Representative', { ns: 'calls' }),
      indicatorColor: theme.colors.warning20,
    },
    [AIIntent.AI_INTENT_SCHEDULE]: {
      name: i18next.t('Scheduling', { ns: 'calls' }),
      indicatorColor: theme.colors.primary50,
    },
    [AIIntent.AI_INTENT_FAQ]: {
      name: i18next.t('FAQ', { ns: 'calls' }),
      indicatorColor: theme.colors.indigo40,
    },
    [AIIntent.AI_INTENT_UNKNOWN]: {
      name: i18next.t('Other', { ns: 'calls' }),
      indicatorColor: theme.colors.neutral40,
    },
  };
  return intentMap[intent] ?? { name: i18next.t('Other', { ns: 'calls' }), indicatorColor: theme.colors.neutral40 };
};
